// PREZENCE — My Activity Page
import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../../api/client';
import toast from 'react-hot-toast';

export default function MyActivity() {
  const [data, setData]   = useState(null);
  const [apps, setApps]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/activity/today'),
      api.get('/apps/summary'),
    ]).then(([act, appData]) => {
      setData(act.data);
      setApps(appData.data.apps || []);
    }).catch(() => toast.error('Failed to load activity'))
    .finally(() => setLoading(false));
  }, []);

  const fmt = m => { const h=Math.floor(m/60), min=m%60; return h>0?`${h}h ${min}m`:`${min}m`; };
  const s = data?.summary || { work:0, break:0, meeting:0, idle:0 };
  const chartData = [
    { name:'Work',    min: s.work,    fill:'#22c55e' },
    { name:'Break',   min: s.break,   fill:'#3ba8ff' },
    { name:'Meeting', min: s.meeting, fill:'#a78bfa' },
    { name:'Idle',    min: s.idle,    fill:'rgba(245,158,11,0.6)' },
  ];

  if (loading) return <div style={{ display:'flex', justifyContent:'center', padding:60 }}><div className="spinner" style={{width:28,height:28}} /></div>;

  return (
    <div style={{ animation:'fadeIn 0.4s ease', maxWidth:900, margin:'0 auto' }}>
      <div className="page-header">
        <h1 className="page-title">My Activity</h1>
        <p className="page-sub">Today's detailed work activity and app usage</p>
      </div>

      <div className="grid-2" style={{ marginBottom:24 }}>
        <div className="card">
          <div className="card-header"><span className="card-title">TIME DISTRIBUTION</span></div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={chartData} margin={{ left:-20 }}>
              <XAxis dataKey="name" tick={{ fill:'#64748b', fontSize:12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill:'#64748b', fontSize:11 }} axisLine={false} tickLine={false} tickFormatter={v=>`${v}m`} />
              <Tooltip contentStyle={{ background:'#0d1520', border:'1px solid rgba(56,189,248,0.2)', borderRadius:8, fontSize:12 }} formatter={v=>[`${v} min`]} />
              {chartData.map((d,i) => <Bar key={i} dataKey="min" data={[d]} fill={d.fill} radius={[4,4,0,0]} />)}
            </BarChart>
          </ResponsiveContainer>
          <div style={{ display:'flex', gap:16, marginTop:8, flexWrap:'wrap' }}>
            {chartData.map((d,i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, color:'#64748b' }}>
                <div style={{ width:8, height:8, borderRadius:2, background:d.fill }} />
                {d.name}: <span style={{ color:'#94a3b8', fontWeight:600, fontFamily:"'JetBrains Mono'" }}>{fmt(d.min)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="card-header"><span className="card-title">APP USAGE TODAY</span></div>
          {apps.length === 0 ? <div className="empty-state" style={{ padding:30 }}><div className="empty-state-icon" style={{ fontSize:28 }}>💻</div><div className="empty-state-text" style={{ fontSize:12 }}>No app data yet today</div></div> :
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {apps.slice(0,6).map((app,i) => (
              <div key={i}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:5 }}>
                  <span style={{ fontSize:12, color:'#94a3b8', fontWeight:600 }}>{app.name}</span>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <span style={{ fontSize:11, color:'#64748b', fontFamily:"'JetBrains Mono'" }}>{app.minutes}m</span>
                    <span style={{ fontSize:11, color: app.is_productive ? '#22c55e' : '#f59e0b', fontFamily:"'JetBrains Mono'" }}>{app.percentage}%</span>
                  </div>
                </div>
                <div className="progress-bar-wrap">
                  <div className="progress-bar-fill" style={{ width:`${app.percentage}%`, background: app.is_productive?'#22c55e':'#f59e0b' }} />
                </div>
              </div>
            ))}
          </div>
          }
        </div>
      </div>

      <div className="card">
        <div className="card-header"><span className="card-title">SESSION TIMELINE</span></div>
        {!data?.sessions?.length ? <div className="empty-state"><div className="empty-state-icon">📊</div><div className="empty-state-text">No sessions recorded today</div></div> :
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {data.sessions.map((s,i) => {
            const colMap = { work:'#22c55e', break:'#3ba8ff', meeting:'#a78bfa', idle:'#f59e0b' };
            const c = colMap[s.session_type] || '#64748b';
            return (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 0', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                <div style={{ width:10, height:10, borderRadius:'50%', background:c, flexShrink:0 }} />
                <div style={{ width:70, fontSize:11, color:'#475569', fontFamily:"'JetBrains Mono'" }}>{new Date(s.started_at).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'})}</div>
                <span className="badge" style={{ background:`${c}20`, color:c, border:`1px solid ${c}40` }}>{s.session_type.charAt(0).toUpperCase()+s.session_type.slice(1)}</span>
                <div style={{ fontSize:12, color:'#64748b' }}>{s.duration_minutes ? `${s.duration_minutes} min` : 'Ongoing...'}</div>
                {s.notes && <div style={{ fontSize:11, color:'#334155', marginLeft:'auto' }}>{s.notes}</div>}
              </div>
            );
          })}
        </div>
        }
      </div>
    </div>
  );
}
